import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Pagination, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { utils } from "@vilocnv/allsetra-core";
import { useAppDispatch, useAppSelector } from "hooks";
import { getUsersBasicInfoThunk } from "app/features";
import { AccountsService } from "app/data/services";
import {
  selectAccountsState,
  selectSpecificSettings
} from "app/data/selectors";
import {
  OperationalHoursCorrection,
  formatOperationalHours,
  resolveCorrectedByName
} from "app/data/helpers/operationalHoursHelpers";
import { OPERATIONAL_HOUR_COUNTER_CONFIG } from "./constants";
import { HistoryList } from "./OperationalHoursCorrectionDisplay.styled";

interface Props {
  accountId: string;
  corrections: OperationalHoursCorrection[];
  loading: boolean;
}

const PAGE_SIZE = 5;

const historyGridSx = {
  display: "grid",
  width: "100%",
  minWidth: 0,
  gridTemplateColumns: "1.15fr 0.85fr 0.75fr 1.35fr 1.1fr",
  columnGap: "12px",
  alignItems: "start"
} as const;

const headerCellSx = {
  color: "text.secondary",
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: 1.4,
  margin: 0,
  minWidth: 0,
  padding: "0 0 8px",
  textAlign: "left"
} as const;

const bodyCellSx = {
  fontSize: "13px",
  lineHeight: 1.4,
  margin: 0,
  minWidth: 0,
  padding: "10px 0",
  textAlign: "left",
  overflowWrap: "anywhere",
  borderTop: "1px solid",
  borderColor: "divider"
} as const;

const OperationalHoursHistory: FC<Props> = ({
  accountId,
  corrections,
  loading
}) => {
  const { t, i18n } = useTranslation(["translation"]);
  const dispatch = useAppDispatch();
  const { usersBasicInfo } = useAppSelector(selectAccountsState);
  const currentUser = useAppSelector(selectSpecificSettings) as {
    uniqueId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  const loadedUsersAccount = useRef<string | null>(null);
  const requestedUserIds = useRef(new Set<string>());
  const [resolvedUsers, setResolvedUsers] = useState<
    Array<{
      uniqueId?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    }>
  >([]);
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(corrections.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleCorrections = corrections.slice(
    currentPage * PAGE_SIZE,
    currentPage * PAGE_SIZE + PAGE_SIZE
  );

  useEffect(() => {
    setPage(0);
  }, [corrections]);

  const lookupUsers = useMemo(() => {
    const signedInUser = currentUser?.uniqueId
      ? [
          {
            uniqueId: currentUser.uniqueId,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            email: currentUser.email
          }
        ]
      : [];

    return [...(usersBasicInfo ?? []), ...signedInUser, ...resolvedUsers];
  }, [currentUser, resolvedUsers, usersBasicInfo]);

  useEffect(() => {
    requestedUserIds.current.clear();
    setResolvedUsers([]);
  }, [accountId]);

  useEffect(() => {
    if (!accountId || loadedUsersAccount.current === accountId) return;

    loadedUsersAccount.current = accountId;
    dispatch(getUsersBasicInfoThunk(accountId));
  }, [accountId, dispatch]);

  useEffect(() => {
    if (!accountId) return;

    const missingIds = Array.from(
      new Set(
        corrections
          .map((correction) => correction.correctedBy)
          .filter((id): id is string => Boolean(id))
      )
    ).filter((id) => {
      const key = id.toLowerCase();
      if (requestedUserIds.current.has(key)) return false;

      return resolveCorrectedByName(id, lookupUsers) === "-";
    });

    if (!missingIds.length) return;

    missingIds.forEach((id) => requestedUserIds.current.add(id.toLowerCase()));
    const requestedForAccount = accountId;

    Promise.all(
      missingIds.map(async (userId) => {
        try {
          const response = await AccountsService.getSpecificAccountUser(
            accountId,
            userId
          );

          return response.status === 200 ? response.data : null;
        } catch {
          return null;
        }
      })
    ).then((rows) => {
      if (loadedUsersAccount.current !== requestedForAccount) return;

      const found = rows.filter(
        (
          row
        ): row is {
          uniqueId?: string;
          firstName?: string;
          lastName?: string;
          email?: string;
        } => Boolean(row)
      );

      if (found.length) {
        setResolvedUsers((current) => [...current, ...found]);
      }
    });
  }, [accountId, corrections, lookupUsers]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";

    return utils.formatDate({
      date: dateString,
      dateFormat: "d MMM yyyy, HH:mm",
      language: i18n.language
    });
  };

  const counterLabel = (counter: OperationalHoursCorrection["counter"]) => {
    const config = OPERATIONAL_HOUR_COUNTER_CONFIG.find(
      (item) => item.counter === counter
    );

    return config ? t(config.labelKey) : counter;
  };

  const hours = (value: number) =>
    t("typography.operationalHoursValue", {
      value: formatOperationalHours(value)
    });

  return (
    <HistoryList data-testid="operational-hours-history">
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {t("typography.operationalHoursHistory")}
      </Typography>

      {loading && corrections.length === 0 ? (
        <Box display="flex" justifyContent="flex-start" py={0.5}>
          <CircularProgress size={16} />
        </Box>
      ) : corrections.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t("typography.noOperationalHoursCorrections")}
        </Typography>
      ) : (
        <>
          <Box sx={historyGridSx}>
            <Typography sx={headerCellSx}>
              {t("typography.operationalHoursCounter")}
            </Typography>
            <Typography sx={headerCellSx}>
              {t("typography.operationalHoursPrevious")}
            </Typography>
            <Typography sx={headerCellSx}>
              {t("typography.operationalHoursNew")}
            </Typography>
            <Typography sx={headerCellSx}>
              {t("typography.operationalHoursWhen")}
            </Typography>
            <Typography sx={headerCellSx}>
              {t("typography.operationalHoursBy")}
            </Typography>

            {visibleCorrections.map((correction, index) => (
              <Box
                key={`${correction.counter}-${correction.correctedAt ?? "none"}-${currentPage}-${index}`}
                sx={{ display: "contents" }}
              >
                <Typography sx={bodyCellSx}>
                  {counterLabel(correction.counter)}
                </Typography>
                <Typography
                  sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }}
                >
                  {hours(correction.previousDisplayed)}
                </Typography>
                <Typography
                  sx={{ ...bodyCellSx, fontVariantNumeric: "tabular-nums" }}
                >
                  {hours(correction.newDisplayed)}
                </Typography>
                <Typography sx={bodyCellSx}>
                  {formatDate(correction.correctedAt)}
                </Typography>
                <Typography sx={bodyCellSx}>
                  {resolveCorrectedByName(correction.correctedBy, lookupUsers)}
                </Typography>
              </Box>
            ))}
          </Box>
          {corrections.length > PAGE_SIZE && (
            <Pagination
              count={pageCount}
              page={currentPage + 1}
              onChange={(_, value) => setPage(value - 1)}
              size="small"
              siblingCount={0}
              boundaryCount={1}
              sx={{ alignSelf: "flex-end", mt: 1 }}
            />
          )}
        </>
      )}
    </HistoryList>
  );
};

export default OperationalHoursHistory;
